import { Link } from 'react-router-dom'
import { cn } from '../../lib/utils'

const OPEN_SIGNUP = import.meta.env.VITE_OPEN_SIGNUP !== 'false'

const FREE_FEATURES = [
  'Up to 5 household members',
  'Family calendar',
  'Task & chore tracking',
  'Shopping lists',
  'Basic meal planning',
  'Notes',
]

const PAID_FEATURES = [
  'Everything in Free',
  'Unlimited members',
  'Budget & bill tracking',
  'Health & allergy logs',
  'Pantry management',
  'Encrypted vault',
  'Stamp reward cards',
  'Linked households',
  'Recipe library & web import',
  'Documents & contacts',
  'Vehicle records',
  'Custody schedule',
]

const LIFETIME_EXTRAS = [
  ...PAID_FEATURES,
  'All future features included',
  'Priority email support',
]

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    cycle: '',
    badge: null,
    features: FREE_FEATURES,
    cta: 'Get started free',
    primary: false,
    href: '/signup',
  },
  {
    id: 'monthly',
    name: 'Monthly',
    price: '$4',
    cycle: '/mo',
    badge: '7-day free trial',
    features: PAID_FEATURES,
    cta: 'Start free trial',
    primary: true,
    href: '/signup',
  },
  {
    id: 'annual',
    name: 'Annual',
    price: '$40',
    cycle: '/yr',
    badge: 'Save 17%',
    features: PAID_FEATURES,
    cta: 'Start free trial',
    primary: false,
    href: '/signup',
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    price: '$120',
    cycle: '',
    badge: 'One-time · Best value',
    features: LIFETIME_EXTRAS,
    cta: 'Buy lifetime access',
    primary: false,
    href: '/signup',
  },
]

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

const PlanCard = ({ plan }) => {
  const ctaHref = OPEN_SIGNUP ? plan.href : '#waitlist'
  const ctaText = OPEN_SIGNUP ? plan.cta : 'Join waitlist'

  return (
    <div className={cn(
      'relative rounded-2xl border p-6 flex flex-col',
      plan.primary
        ? 'border-primary bg-primary text-white shadow-2xl shadow-primary/25 scale-[1.02]'
        : 'border-border bg-white',
    )}>
      {plan.badge && (
        <div className={cn(
          'absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap',
          plan.primary ? 'bg-white text-primary' : 'bg-primary text-white',
        )}>
          {plan.badge}
        </div>
      )}

      <div className="mb-5">
        <div className={cn('text-xs font-semibold uppercase tracking-wider mb-2', plan.primary ? 'text-white/60' : 'text-text-muted')}>
          {plan.name}
        </div>
        <div className="flex items-end gap-1">
          <span className="text-4xl font-bold">{plan.price}</span>
          {plan.cycle && <span className={cn('text-sm mb-1.5', plan.primary ? 'text-white/60' : 'text-text-muted')}>{plan.cycle}</span>}
        </div>
        {plan.id === 'annual' && (
          <p className={cn('text-xs mt-1', plan.primary ? 'text-white/60' : 'text-text-muted')}>$3.33/mo billed annually</p>
        )}
      </div>

      <ul className="space-y-2 flex-1 mb-6">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <span className={cn('mt-0.5 flex-shrink-0', plan.primary ? 'text-white' : 'text-emerald-500')}>
              <CheckIcon />
            </span>
            <span className={plan.primary ? 'text-white/85' : 'text-text-secondary'}>{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to={ctaHref}
        className={cn(
          'block text-center text-sm font-semibold px-4 py-3 rounded-xl transition-all',
          plan.primary
            ? 'bg-white text-primary hover:bg-white/90 shadow-lg'
            : 'bg-primary text-white hover:bg-primary/90',
        )}
      >
        {ctaText}
      </Link>
    </div>
  )
}

export const PricingSection = () => (
  <section id="pricing" className="py-24 px-4 bg-background">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-14 reveal">
        <p className="text-primary text-sm font-semibold uppercase tracking-wider mb-3">Pricing</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-text-primary mb-4">
          Simple, honest pricing
        </h2>
        <p className="text-text-secondary max-w-xl mx-auto">
          Start free. Upgrade when you need more. One household subscription covers every member.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-start reveal">
        {PLANS.map((plan) => <PlanCard key={plan.id} plan={plan} />)}
      </div>

      <div className="mt-10 text-center text-xs text-text-muted space-y-1 reveal">
        <p>All prices in USD · Cancel anytime · No hidden fees</p>
        <p>One subscription covers your entire household — no per-seat pricing</p>
      </div>
    </div>
  </section>
)
